import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  getSubCategory,
  updateSubCategory,
} from "../../services/subcategory.service";
import { getAllMainCategories } from "../../services/category.service";
import Select from "react-select";

export default function SubCategoryUpdate() {
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading2, setIsLoading2] = useState(false);
  const { token } = useSelector((state) => state?.auth);
  const [uploadImage, setUploadImage] = useState(null);
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategory, setSubCategory] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const subCategoryId = params.get("id");

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm();

  const fetchMainCategories = async () => {
    try {
      const res = await getAllMainCategories(token);
      if (res && res.status >= 200 && res.status < 300) {
        setMainCategories(res?.data?.results || []);
      } else {
        Swal.fire(
          "Error",
          "Failed to fetch main categories. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching main categories:", error);
    }
  };

  const fetchSubCategory = async () => {
    try {
      const res = await getSubCategory(token, subCategoryId);
      if (res && res.status >= 200 && res.status < 300) {
        setSubCategory(res?.data);
      } else {
        Swal.fire(
          "Error",
          "Failed to fetch sub-category details. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching sub-category:", error);
    }
  };

  useEffect(() => {
    fetchMainCategories();
    if (subCategoryId) {
      fetchSubCategory();
    }
  }, [subCategoryId]);

  useEffect(() => {
    if (subCategory) {
      setValue("subCategoryName", subCategory?.sub_category_name);
      setValue("mainCategory", subCategory?.main_category);
      setValue("description", subCategory?.description);
      setImagePreview(subCategory?.image);
    }
  }, [subCategory]);

  const onSubmit = async (item) => {
    setIsLoading2(true);

    const data = {
      sub_category_name: item.subCategoryName,
      main_category: item.mainCategory,
      image: uploadImage,
      description: item.description,
    };

    try {
      const res = await updateSubCategory(token, data, subCategoryId);

      if (res && res.status >= 200 && res.status < 300) {
        Swal.fire({
          title: "Updated!",
          text: `${item.subCategoryName} Sub-Category has been updated successfully.`,
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/sub-categories");
        });
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Invalid input. Please check your data.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error during updating sub-category:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error || "A network or server error occurred. Please try again later.",
        "error"
      );
    } finally {
      setIsLoading2(false);
    }
  };

   const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (
        ["image/jpeg", "image/png", "image/jpg", "image/webp"].includes(
          file.type
        )
      ) {
        if (file.size < 1 * 1024 * 1024) {
          setImagePreview(URL.createObjectURL(file));
          setUploadImage(file);
          clearErrors("image");
        } else {
          setError("image", {
            type: "manual",
            message: "Image size must be less than 1MB.",
          });
          setUploadImage(null);
          setValue("image", null);
        }
      } else {
        setError("image", {
          type: "manual",
          message: "Only JPEG, PNG, JPG, or WEBP files are allowed.",
        });
        setUploadImage(null);
        setValue("image", null);
      }
    }
  };

  return (
    <div className="content-body">
      <div className="container-fluid">
        <div className="row">
          <div className="col-xl-12 col-lg-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title">Update Sub Category</h4>
                <Link to="/sub-categories">
                  <img
                    src="/assets/images/kidskart/icon/back-button.png"
                    alt="Back"
                  />
                </Link>
              </div>
              <div className="card-body py-3">
                <div className="basic-form">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                      {/* Main Category */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">Main Category</label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <Select
                          options={mainCategories.map((category) => ({
                            value: category.category_id,
                            label: category.category_name,
                          }))}
                          {...register("mainCategory", {
                            required: "Main Category is required",
                          })}
                          onChange={(selectedOption) => {
                            setValue("mainCategory", selectedOption?.value);
                            clearErrors("mainCategory");
                          }}
                          placeholder="Select Category"
                          isClearable
                          value={mainCategories
                            .map((category) => ({
                              value: category.category_id,
                              label: category.category_name,
                            }))
                            .find(
                              (option) => option.value === watch("mainCategory")
                            )}
                        />
                        {errors.mainCategory && (
                          <p className="error">{errors.mainCategory.message}</p>
                        )}
                      </div>

                      {/* Sub Category Name */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">
                            Sub Category Name
                          </label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter category name"
                          {...register("subCategoryName", {
                            required: "Sub Category is required",
                          })}
                        />
                        {errors.subCategoryName && (
                          <p className="error">
                            {errors.subCategoryName.message}
                          </p>
                        )}
                      </div>

                      {/* Image */}
                      <div className="mb-4 col-md-6">
                        <div className="d-flex">
                          <label className="form-label">Image</label>
                          <i class="fa-solid fa-asterisk asterisk ms-1 mt-1"></i>
                        </div>
                        <input
                          type="file"
                          className="form-control"
                          {...register("image")}
                          onChange={handleImageChange}
                        />
                        {errors.image && (
                          <p className="error">{errors.image.message}</p>
                        )}
                      </div>
                      <div className="mb-4 col-md-6">
                        {imagePreview && (
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="img-thumbnail mt-2"
                            style={{ width: "100px" }}
                          />
                        )}
                      </div>

                      {/* Description */}
                      <div className="mb-4 col-md-12">
                        <label className="form-label">Description</label>
                        <textarea
                          rows="3"
                          className="form-control"
                          placeholder="Enter description"
                          {...register("description")}
                        ></textarea>
                        {errors.description && (
                          <p className="error">{errors.description.message}</p>
                        )}
                      </div>
                    </div>

                    {!isLoading2 ? (
                      <div className="col-sm-4">
                        <button type="submit" className="btn btn-primary w-100">
                          UPDATE CATEGORY
                        </button>
                      </div>
                    ) : (
                      <div className="col-sm-4">
                        <button
                          type="button"
                          className="btn btn-primary w-100"
                          disabled
                        >
                          UPDATING CATEGORY
                        </button>

                        <div
                          className="spinner-border2 text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
